import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, GitCompare, AlertTriangle, CheckCircle2, XCircle, Loader2, ChevronRight, Film } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const KIND_LABEL = {
    SAFETY_CHANGED: "Safety changed",
    PROCEDURE_STEP_CHANGED: "Procedure step changed",
    PARAMETER_CHANGED: "Parameter changed",
    IMAGE_CHANGED: "Image changed",
    FORMULA_CHANGED: "Formula changed",
    STEP_ADDED: "Step added",
    STEP_REMOVED: "Step removed",
    SECTION_CHANGED: "Section changed",
    METADATA_CHANGED: "Metadata changed",
};

const SEV_STYLE = {
    HIGH: "border-red-300 bg-red-50 text-red-900",
    MEDIUM: "border-amber-300 bg-amber-50 text-amber-900",
    LOW: "border-neutral-300 bg-neutral-50 text-neutral-700",
};

function SeverityBadge({ s }) {
    const cls = {
        HIGH: "bg-red-100 text-red-800 border-red-200",
        MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
        LOW: "bg-neutral-100 text-neutral-700 border-neutral-200",
    }[s] || "bg-neutral-100 text-neutral-700";
    return <Badge className={`text-[10px] uppercase tracking-wider ${cls}`}>{s}</Badge>;
}

export function RevisionsList() {
    const [rows, setRows] = useState(null);
    const nav = useNavigate();
    useEffect(() => {
        api.get("/revisions").then((r) => setRows(r.data)).catch((e) => {
            toast.error(formatApiError(e));
            setRows([]);
        });
    }, []);
    if (rows === null) return <div className="text-neutral-500">Loading…</div>;
    return (
        <div data-testid="page-revisions-list">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Document revisions</h1>
                <p className="text-sm text-neutral-500 mt-0.5">
                    Automatic diffs between old and new versions of an approved document. Apply a plan to flag only the affected training scenes for regeneration.
                </p>
            </div>
            {rows.length === 0 ? (
                <Card><CardContent className="py-14 text-center text-neutral-500" data-testid="revisions-empty">
                    No revisions yet. Upload a new version of a document from Documents → detail to create one.
                </CardContent></Card>
            ) : (
                <div className="space-y-2">
                    {rows.map((p) => {
                        const total = (p.changes || []).length;
                        const high = (p.changes || []).filter((c) => c.severity === "HIGH").length;
                        return (
                            <Card key={p.id} data-testid={`revision-row-${p.id}`}
                                  className="hover:border-neutral-400 transition-colors cursor-pointer"
                                  onClick={() => nav(`/knowledge/revisions/${p.id}`)}>
                                <CardContent className="p-4 flex items-center gap-4">
                                    <GitCompare size={18} className="text-neutral-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-neutral-900 truncate">
                                            {p.old_revision_label || "—"} → {p.new_revision_label || "—"}
                                        </div>
                                        <div className="text-xs text-neutral-500 mt-0.5">
                                            {total} change{total !== 1 ? "s" : ""} · {high} high-severity · {(p.scene_impacts || []).length} scene{p.scene_impacts?.length !== 1 ? "s" : ""} impacted · {(p.impacted_modules || []).length} module{p.impacted_modules?.length !== 1 ? "s" : ""}
                                        </div>
                                    </div>
                                    <Badge className={
                                        p.status === "DRAFT" ? "bg-blue-100 text-blue-800 border-blue-200" :
                                        p.status === "APPLIED" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                                        "bg-neutral-100 text-neutral-600 border-neutral-200"
                                    }>{p.status}</Badge>
                                    <ChevronRight size={16} className="text-neutral-400" />
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function RevisionCompare() {
    const { id } = useParams();
    const nav = useNavigate();
    const [plan, setPlan] = useState(null);
    const [applying, setApplying] = useState(false);
    const [selectedScenes, setSelectedScenes] = useState(new Set());
    const [selectedChangeId, setSelectedChangeId] = useState(null);

    async function load() {
        try {
            const r = await api.get(`/revisions/${id}`);
            setPlan(r.data);
            setSelectedScenes(new Set((r.data.scene_impacts || []).map((s) => s.scene_id)));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    if (!plan) return <div className="text-neutral-500">Loading…</div>;

    const changesById = Object.fromEntries((plan.changes || []).map((c) => [c.id, c]));
    const selectedChange = selectedChangeId ? changesById[selectedChangeId] : null;
    const toggleScene = (sid) => {
        const s = new Set(selectedScenes);
        if (s.has(sid)) s.delete(sid); else s.add(sid);
        setSelectedScenes(s);
    };

    async function apply() {
        setApplying(true);
        try {
            const r = await api.post(`/revisions/${plan.id}/apply`, {
                scene_ids: Array.from(selectedScenes),
                reason: "Revision retraining",
            });
            toast.success(`Flagged ${r.data.apply_summary?.scenes_touched || 0} scenes for regeneration`);
            setPlan(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setApplying(false);
        }
    }
    async function dismiss() {
        try {
            const r = await api.post(`/revisions/${plan.id}/dismiss`);
            toast.success("Plan dismissed");
            setPlan(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const counts = plan.counts || {};
    const isDraft = plan.status === "DRAFT";

    return (
        <div data-testid="page-revision-compare">
            <div className="mb-4 flex items-center gap-3">
                <Button size="sm" variant="ghost" onClick={() => nav("/knowledge/revisions")} data-testid="btn-back">
                    <ArrowLeft size={14} className="mr-1" />Back
                </Button>
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
                        {plan.old_revision_label || "—"} → {plan.new_revision_label || "—"}
                    </h1>
                    <p className="text-sm text-neutral-500 mt-0.5">
                        {(plan.changes || []).length} changes detected · {(plan.scene_impacts || []).length} affected scenes across {(plan.impacted_modules || []).length} module{plan.impacted_modules?.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <Badge className={
                        plan.status === "DRAFT" ? "bg-blue-100 text-blue-800 border-blue-200" :
                        plan.status === "APPLIED" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                        "bg-neutral-100 text-neutral-600 border-neutral-200"
                    } data-testid="plan-status">{plan.status}</Badge>
                    {isDraft && (
                        <>
                            <Button size="sm" variant="outline" onClick={dismiss} data-testid="btn-dismiss">
                                <XCircle size={14} className="mr-1.5" />Dismiss
                            </Button>
                            <Button size="sm" onClick={apply} disabled={applying || selectedScenes.size === 0}
                                    data-testid="btn-apply-plan">
                                {applying ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                                Apply to {selectedScenes.size} scene{selectedScenes.size !== 1 ? "s" : ""}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Counts row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
                {Object.entries(counts).map(([k, n]) => (
                    <Card key={k} data-testid={`count-${k}`}>
                        <CardContent className="p-3">
                            <div className="text-2xl font-semibold text-neutral-900">{n}</div>
                            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-0.5">{KIND_LABEL[k] || k}</div>
                        </CardContent>
                    </Card>
                ))}
                {Object.keys(counts).length === 0 && (
                    <Card><CardContent className="p-3 text-sm text-neutral-500" data-testid="no-changes">No changes detected between the two versions.</CardContent></Card>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Changes list */}
                <div>
                    <h2 className="text-sm font-semibold text-neutral-900 mb-2">Detected changes</h2>
                    <div className="space-y-2">
                        {(plan.changes || []).length === 0 && (
                            <Card><CardContent className="p-4 text-sm text-neutral-500">No changes detected.</CardContent></Card>
                        )}
                        {(plan.changes || []).map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setSelectedChangeId(c.id)}
                                data-testid={`change-${c.id}`}
                                className={`w-full text-left rounded-md border p-3 transition-colors ${
                                    selectedChangeId === c.id
                                        ? "border-neutral-900 bg-white"
                                        : `${SEV_STYLE[c.severity]} hover:border-neutral-500`
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    {c.severity === "HIGH" && <AlertTriangle size={13} className="text-red-600" />}
                                    <span className="text-xs font-semibold uppercase tracking-wider">{KIND_LABEL[c.kind] || c.kind}</span>
                                    <SeverityBadge s={c.severity} />
                                    {(c.old_step_number || c.new_step_number) && (
                                        <span className="text-xs text-neutral-500 ml-auto">Step {c.new_step_number || c.old_step_number}</span>
                                    )}
                                </div>
                                <div className="text-xs text-neutral-700 line-clamp-2">{c.diff_summary}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Detail + impact */}
                <div>
                    <h2 className="text-sm font-semibold text-neutral-900 mb-2">
                        {selectedChange ? "Change detail" : "Impacted scenes"}
                    </h2>
                    {selectedChange ? (
                        <Card><CardContent className="p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wider">{KIND_LABEL[selectedChange.kind] || selectedChange.kind}</span>
                                <SeverityBadge s={selectedChange.severity} />
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Was</div>
                                    <div className="rounded-md border border-red-200 bg-red-50 p-2.5 whitespace-pre-wrap text-neutral-800 min-h-[80px]" data-testid="diff-old">
                                        {selectedChange.old_text || <span className="text-neutral-400">—</span>}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Now</div>
                                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 whitespace-pre-wrap text-neutral-800 min-h-[80px]" data-testid="diff-new">
                                        {selectedChange.new_text || <span className="text-neutral-400">—</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="pt-2">
                                <Button size="sm" variant="ghost" onClick={() => setSelectedChangeId(null)} data-testid="btn-back-to-impacts">
                                    ← Back to impacted scenes
                                </Button>
                            </div>
                        </CardContent></Card>
                    ) : (
                        <div className="space-y-2">
                            {(plan.scene_impacts || []).length === 0 && (
                                <Card><CardContent className="p-4 text-sm text-neutral-500" data-testid="no-impacts">
                                    No scenes reference the changed content. Nothing to regenerate.
                                </CardContent></Card>
                            )}
                            {(plan.scene_impacts || []).map((s) => (
                                <label key={s.scene_id} className="block cursor-pointer" data-testid={`impact-${s.scene_id}`}>
                                    <Card className="hover:border-neutral-400 transition-colors">
                                        <CardContent className="p-3 flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedScenes.has(s.scene_id)}
                                                onChange={() => toggleScene(s.scene_id)}
                                                disabled={!isDraft}
                                                data-testid={`chk-${s.scene_id}`}
                                                className="mt-0.5"
                                            />
                                            <Film size={14} className="text-neutral-400 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-neutral-900 truncate">
                                                    Scene {s.scene_number} · {s.chapter_name || "Chapter"}
                                                </div>
                                                <div className="text-xs text-neutral-500 truncate">
                                                    {s.module_name || s.module_id} · {s.reason}
                                                </div>
                                            </div>
                                            <SeverityBadge s={s.highest_severity} />
                                        </CardContent>
                                    </Card>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {plan.status === "APPLIED" && plan.apply_summary && (
                <Card className="mt-6 border-emerald-200 bg-emerald-50" data-testid="applied-summary">
                    <CardContent className="p-4 text-sm text-emerald-900">
                        <CheckCircle2 size={14} className="inline mr-1.5" />
                        Applied · {plan.apply_summary.scenes_touched || 0} scenes flagged across {plan.apply_summary.modules_touched || 0} modules. Regenerate them from the Storyboards / Video Studio pages.
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
